// Active tenant users for assignee pickers and name lookups. Shares the
// `users-for-assign` query key with AssignChecklistButton's own fetch, so
// TanStack makes this one network call regardless of how many callers mount
// — every consumer of that key must keep this exact select shape.
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface AssignableUser {
  id: string
  name: string
}

/**
 * Name lookup for user-id columns on already-fetched rows, inside a queryFn.
 * Exists because the HSE/NC audit columns (reported_by, closed_by, …) FK
 * auth.users, which PostgREST cannot embed across — `users!<fkey>(name)`
 * has no relationship in the schema cache and 400s the whole query.
 * Degrades to an empty map on error: the names are decoration, and a
 * lookup failure must not take down the register they decorate.
 */
export async function fetchUserNames(ids: Array<string | null | undefined>): Promise<Map<string, string>> {
  const distinct = [...new Set(ids.filter(Boolean))] as string[]
  if (distinct.length === 0) return new Map()
  const { data, error } = await supabase.from('users')
    .select('id, name').in('id', distinct)
  if (error) return new Map()
  return new Map((data as AssignableUser[]).map(u => [u.id, u.name]))
}

/**
 * `loaded` = the list read has produced rows (the readState rule: data !==
 * undefined, so a failed background refetch keeps it true). `failed` = it
 * never did. A picker needs both to tell "not in the list" from "list not
 * read yet": a saved person missing from an UNREAD list is not a former member.
 */
export function useAssigneeNames(): {
  users: AssignableUser[]; byId: Map<string, string>; loaded: boolean; failed: boolean
} {
  const q = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: async () => {
      const { data, error } = await supabase.from('users')
        .select('id, name').eq('is_active', true).order('name')
      if (error) throw error
      return data as AssignableUser[]
    },
  })
  const users = useMemo(() => q.data ?? [], [q.data])
  const byId = useMemo(() => new Map(users.map(u => [u.id, u.name])), [users])
  const loaded = q.data !== undefined
  return { users, byId, loaded, failed: !loaded && q.isError }
}
